const DEFAULT_ERP_USER_HINT = '输入真实姓名、用户名或 ERP 用户 ID，选择后会按该 ERP 用户对应的店铺自动同步到成员。';

/**
 * 成员编辑弹窗的状态、ERP 用户搜索与保存编排。
 * 目录列表控制器只注入请求、提示、刷新和安全 DOM 工厂，不持有弹窗实现细节。
 */
export function createUserEditModalController({ authFetch, showToast, reloadActiveUserView, createElement }) {
  let editingUser = null;
  let erpUserSearchTimer = null;
  let erpUserSearchSeq = 0;
  let erpUserSearchCache = { keyword: '', items: null, meta: null };

  function getElement(id) {
    return document.getElementById(id);
  }

  function hideErpUserResults() {
    const resultsEl = getElement('user-edit-erp-results');
    if (!resultsEl) return;
    resultsEl.replaceChildren();
    resultsEl.classList.remove('active');
  }

  function hideUserEditModal() {
    const modal = getElement('user-edit-modal');
    if (modal) modal.hidden = true;
    hideErpUserResults();
    editingUser = null;
  }

  function isValidErpUserSearchKeyword(keyword) {
    return Boolean(keyword) && (/^\d+$/.test(keyword) || /[\u3400-\u9fff]/.test(keyword) || keyword.length >= 2);
  }

  function renderErpUserResults(items = [], meta = {}) {
    const resultsEl = getElement('user-edit-erp-results');
    if (!resultsEl) return;
    if (!items.length) {
      const message = meta.message || '没有匹配的 ERP 用户';
      const empty = createElement('div', 'erp-user-option erp-user-option--empty', message);
      resultsEl.replaceChildren(empty);
      resultsEl.classList.add('active');
      return;
    }

    const options = items.map((item) => {
      const main = [item.realName, item.username].filter(Boolean).join(' / ') || (item.source === 'selectdb-bindings' ? 'ERP 用户 ID' : '未命名 ERP 用户');
      const detail = [item.status, item.roleName, item.organizationName, item.teamName].filter(Boolean).join(' · ');
      const option = createElement('button', 'erp-user-option');
      option.type = 'button';
      option.dataset.erpUserId = String(item.erpUserId || '');
      option.dataset.realName = String(item.realName || '');
      option.dataset.username = String(item.username || '');
      option.appendChild(createElement('span', 'erp-user-main', main));
      option.appendChild(createElement('span', 'erp-user-id', String(item.erpUserId || '')));
      option.appendChild(createElement('span', 'erp-user-meta', detail || '无组织信息'));
      return option;
    });
    resultsEl.replaceChildren(...options);
    resultsEl.classList.add('active');
  }

  function scheduleErpUserSearch(keyword, { delay = 220 } = {}) {
    const hintEl = getElement('user-edit-erp-hint');
    if (erpUserSearchTimer) clearTimeout(erpUserSearchTimer);
    const seq = ++erpUserSearchSeq;
    erpUserSearchTimer = setTimeout(async () => {
      try {
        const resp = await authFetch(`/api/admin/erp-users/search?q=${encodeURIComponent(keyword)}&limit=8`);
        if (seq !== erpUserSearchSeq) return;
        if (!resp.ok) {
          const data = await resp.json().catch(() => ({}));
          throw new Error(data.error || '搜索 ERP 用户失败');
        }
        const data = await resp.json();
        erpUserSearchCache = { keyword, items: data.items || [], meta: data };
        renderErpUserResults(erpUserSearchCache.items, data);
        if (hintEl) hintEl.textContent = data.message || DEFAULT_ERP_USER_HINT;
      } catch (error) {
        if (seq !== erpUserSearchSeq) return;
        hideErpUserResults();
        if (hintEl) hintEl.textContent = error.message || '搜索 ERP 用户失败';
      }
    }, delay);
  }

  function handleErpUserSearchInput(event) {
    const keyword = event.target.value.trim();
    const hintEl = getElement('user-edit-erp-hint');
    if (erpUserSearchTimer) clearTimeout(erpUserSearchTimer);
    if (!isValidErpUserSearchKeyword(keyword)) {
      hideErpUserResults();
      if (hintEl) hintEl.textContent = DEFAULT_ERP_USER_HINT;
      return;
    }
    scheduleErpUserSearch(keyword, { delay: 220 });
  }

  function handleErpUserSearchFocus(event) {
    const keyword = event.target.value.trim();
    if (!isValidErpUserSearchKeyword(keyword)) return;
    if (erpUserSearchCache.keyword === keyword && Array.isArray(erpUserSearchCache.items)) {
      renderErpUserResults(erpUserSearchCache.items, erpUserSearchCache.meta || {});
      return;
    }
    scheduleErpUserSearch(keyword, { delay: 0 });
  }

  function handleErpUserResultClick(event) {
    const option = event.target.closest('.erp-user-option[data-erp-user-id]');
    if (!option) return;
    const erpUserId = option.dataset.erpUserId || '';
    const label = [option.dataset.realName, option.dataset.username].filter(Boolean).join(' / ');
    const input = getElement('user-edit-erp-user-id');
    if (input) input.value = erpUserId;
    const hintEl = getElement('user-edit-erp-hint');
    if (hintEl) hintEl.textContent = `已选择 ${label || 'ERP 用户'}（${erpUserId}），保存后完成绑定。`;
    hideErpUserResults();
  }

  async function open({ userId, userName, currentRole = '', focusGroupOnly = false, focusErpOnly = false }) {
    try {
      const [groupsResp, usersResp] = await Promise.all([
        authFetch('/api/groups'),
        authFetch('/api/admin/users?page=1&size=500'),
      ]);
      if (!groupsResp.ok || !usersResp.ok) throw new Error('加载用户编辑数据失败');

      const groupsData = await groupsResp.json();
      const usersData = await usersResp.json();
      const groups = groupsData.groups || [];
      const users = usersData.users || [];
      const user = users.find((item) => Number(item.id) === Number(userId));
      if (!user) throw new Error('用户不存在或已被删除');

      const roleSelect = getElement('user-edit-role');
      const groupSelect = getElement('user-edit-group');
      const erpInput = getElement('user-edit-erp-user-id');
      const erpHint = getElement('user-edit-erp-hint');
      const displayName = userName || user.name || user.email || `#${userId}`;
      getElement('user-edit-title').textContent = `编辑用户：${displayName}`;
      roleSelect.value = currentRole || user.role || 'member';
      groupSelect.replaceChildren(
        new Option('不分组', ''),
        ...groups.map((group) => new Option(String(group.name || ''), String(group.id ?? ''))),
      );
      groupSelect.value = user.groupId ? String(user.groupId) : '';
      erpInput.value = user.erpUserId ? String(user.erpUserId) : '';
      hideErpUserResults();
      erpHint.textContent = focusErpOnly
        ? DEFAULT_ERP_USER_HINT
        : 'ERP 用户 ID 可由组长或管理员在审批通过后绑定。';

      const roleField = roleSelect.closest('.modal-field');
      const groupField = groupSelect.closest('.modal-field');
      const erpField = erpInput.closest('.modal-field');
      if (roleField) roleField.hidden = focusErpOnly;
      if (groupField) groupField.hidden = focusErpOnly;
      if (erpField) erpField.hidden = false;

      editingUser = {
        userId: Number(userId),
        userName: displayName,
        currentRole: user.role,
        currentGroupId: user.groupId ? Number(user.groupId) : 0,
        currentErpUserId: user.erpUserId ? Number(user.erpUserId) : null,
        focusErpOnly,
      };
      const modal = getElement('user-edit-modal');
      if (modal) modal.hidden = false;
      if (focusErpOnly) {
        erpInput.focus();
      } else if (focusGroupOnly) {
        groupSelect.focus();
      } else {
        roleSelect.focus();
      }
    } catch (error) {
      console.error('Error opening user edit modal:', error);
      showToast(error.message || '打开用户编辑失败', 'error');
    }
  }

  async function submit() {
    if (!editingUser) return;
    const role = getElement('user-edit-role').value;
    const groupValue = getElement('user-edit-group').value;
    const groupId = groupValue ? Number(groupValue) : 0;
    const erpUserIdRaw = getElement('user-edit-erp-user-id').value.trim();
    const erpUserId = erpUserIdRaw ? Number(erpUserIdRaw) : 0;
    const { userId, userName, currentRole, currentGroupId, currentErpUserId, focusErpOnly } = editingUser;

    try {
      if (focusErpOnly && !erpUserId) throw new Error('请先填写 ERP 用户 ID');
      if (erpUserIdRaw && !Number.isFinite(erpUserId)) throw new Error('请从 ERP 用户搜索结果中选择一个有效用户');

      if (!focusErpOnly && role !== currentRole) {
        const roleResp = await authFetch(`/api/admin/users/${userId}/role`, {
          method: 'PUT',
          body: JSON.stringify({ role }),
        });
        if (!roleResp.ok) {
          const data = await roleResp.json();
          throw new Error(data.error || '角色更新失败');
        }
      }

      if (!focusErpOnly && groupId !== currentGroupId) {
        if (currentGroupId > 0) {
          const removeResp = await authFetch(`/api/groups/${currentGroupId}/members/${userId}`, { method: 'DELETE' });
          if (!removeResp.ok) {
            const data = await removeResp.json();
            throw new Error(data.error || '移出原分组失败');
          }
        }
        if (groupId > 0) {
          const addResp = await authFetch(`/api/groups/${groupId}/members`, {
            method: 'POST',
            body: JSON.stringify({ userId }),
          });
          if (!addResp.ok) {
            const data = await addResp.json();
            throw new Error(data.error || '分配新分组失败');
          }
        }
      }

      if (erpUserId && erpUserId !== currentErpUserId) {
        const bindingResp = await authFetch(`/api/admin/users/${userId}/erp-binding`, {
          method: 'PUT',
          body: JSON.stringify({ erpUserId }),
        });
        if (!bindingResp.ok) {
          const data = await bindingResp.json();
          throw new Error(data.error || 'ERP 绑定失败');
        }
      } else if (focusErpOnly && erpUserId === currentErpUserId) {
        throw new Error('该成员已绑定当前 ERP 用户 ID');
      }

      showToast(`用户「${userName}」已更新`, 'success');
      hideUserEditModal();
      await reloadActiveUserView();
    } catch (error) {
      console.error('Error submitting user edit modal:', error);
      showToast(error.message || '更新用户失败', 'error');
    }
  }

  function bindDomEvents() {
    getElement('user-edit-cancel')?.addEventListener('click', hideUserEditModal);
    getElement('user-edit-save')?.addEventListener('click', submit);
    getElement('user-edit-erp-user-id')?.addEventListener('input', handleErpUserSearchInput);
    getElement('user-edit-erp-user-id')?.addEventListener('focus', handleErpUserSearchFocus);
    getElement('user-edit-erp-results')?.addEventListener('click', handleErpUserResultClick);
  }

  return { bindDomEvents, open };
}
